import React,{useState}from"react";
import{Box,Button,Dialog,DialogActions,DialogContent,DialogTitle,FormControl,InputLabel,MenuItem,Select,TextField}from"@mui/material";
import AddRoundedIcon from"@mui/icons-material/AddRounded";
import SwapHorizRoundedIcon from"@mui/icons-material/SwapHorizRounded";
import AccountBalanceWalletRoundedIcon from"@mui/icons-material/AccountBalanceWalletRounded";
import{useAlert}from"../../../context/AlertContext";

const API_BASE="/api/treasury";
const initialManual={tipo:"EGRESO",medio:"EFECTIVO",monto:"",concepto:"",observacion:""};
const initialTransfer={medio_origen:"EFECTIVO",medio_destino:"TRANSFERENCIA",monto:"",concepto:"Traspaso entre fondos",referencia:"",observacion:""};
const initialBalance={efectivo:"",transferencia:"",observacion:""};

function getToken(){
  return localStorage.getItem("accessToken")||localStorage.getItem("access")||"";
}

function normalizeDecimalInput(value,maxDecimals=2){
  let text=String(value??"").replace(/,/g,".").replace(/[^\d.]/g,"");
  const firstDot=text.indexOf(".");
  if(firstDot!==-1)text=text.slice(0,firstDot+1)+text.slice(firstDot+1).replace(/\./g,"");
  if(text.startsWith("."))text=`0${text}`;
  if(!text)return"";
  const parts=text.split(".");
  let integer=parts[0].replace(/^0+(?=\d)/,"");
  if(parts.length>1)return`${integer||"0"}.${parts[1].slice(0,maxDecimals)}`;
  return integer;
}

function decimalNumber(value){
  const number=Number(String(value??"").replace(",","."));
  return Number.isFinite(number)?number:0;
}

function errorMessage(data,fallback){
  const value=data?.error||data?.detail;
  if(typeof value==="string")return value;
  if(Array.isArray(value))return value.join(" ");
  if(value&&typeof value==="object")return Object.values(value).flat().join(" ");
  return fallback;
}

async function responseData(response){
  try{return await response.json();}catch{return{};}
}

export default function TreasuryModals({dashboardTheme,onReload,canCreate=true}){
  const{showError,showSuccess}=useAlert();
  const token=getToken();
  const[manualModal,setManualModal]=useState(false);
  const[manualForm,setManualForm]=useState(initialManual);
  const[transferModal,setTransferModal]=useState(false);
  const[transferForm,setTransferForm]=useState(initialTransfer);
  const[balanceModal,setBalanceModal]=useState(false);
  const[balanceForm,setBalanceForm]=useState(initialBalance);
  const[saving,setSaving]=useState(false);

  const authHeaders={Authorization:`Bearer ${token}`};

  const reload=async()=>{
    if(typeof onReload==="function")await onReload();
  };

  const closeManual=()=>{
    if(saving)return;
    setManualModal(false);
    setManualForm(initialManual);
  };

  const closeTransfer=()=>{
    if(saving)return;
    setTransferModal(false);
    setTransferForm(initialTransfer);
  };

  const closeBalance=()=>{
    if(saving)return;
    setBalanceModal(false);
    setBalanceForm(initialBalance);
  };

  const updateManual=(field,value)=>{
    setManualForm(prev=>({...prev,[field]:field==="monto"?normalizeDecimalInput(value):value}));
  };

  const updateTransfer=(field,value)=>{
    setTransferForm(prev=>({...prev,[field]:field==="monto"?normalizeDecimalInput(value):value}));
  };

  const updateBalance=(field,value)=>{
    setBalanceForm(prev=>({...prev,[field]:["efectivo","transferencia"].includes(field)?normalizeDecimalInput(value):value}));
  };

  const saveManualMovement=async()=>{
    const monto=decimalNumber(manualForm.monto);
    if(monto<=0){showError("Ingresa un monto mayor a cero");return;}
    if(!manualForm.concepto.trim()){showError("Ingresa el motivo o concepto");return;}
    try{
      setSaving(true);
      const response=await fetch(`${API_BASE}/movimientos/manual/`,{
        method:"POST",
        headers:{...authHeaders,"Content-Type":"application/json"},
        body:JSON.stringify({...manualForm,monto:manualForm.monto||"0"}),
        credentials:"include"
      });
      const data=await responseData(response);
      if(!response.ok){showError(errorMessage(data,"No se pudo registrar el movimiento"));return;}
      showSuccess(data.mensaje||"Movimiento registrado correctamente");
      setManualModal(false);
      setManualForm(initialManual);
      await reload();
    }catch{
      showError("Error de conexión");
    }finally{
      setSaving(false);
    }
  };

  const saveTransfer=async()=>{
    const monto=decimalNumber(transferForm.monto);
    if(monto<=0){showError("Ingresa un monto mayor a cero");return;}
    if(transferForm.medio_origen===transferForm.medio_destino){showError("El fondo de origen y destino deben ser diferentes");return;}
    try{
      setSaving(true);
      const response=await fetch(`${API_BASE}/traspasos/`,{
        method:"POST",
        headers:{...authHeaders,"Content-Type":"application/json"},
        body:JSON.stringify({...transferForm,monto:transferForm.monto||"0"}),
        credentials:"include"
      });
      const data=await responseData(response);
      if(!response.ok){showError(errorMessage(data,"No se pudo registrar el traspaso"));return;}
      showSuccess(data.mensaje||"Traspaso registrado correctamente");
      setTransferModal(false);
      setTransferForm(initialTransfer);
      await reload();
    }catch{
      showError("Error de conexión");
    }finally{
      setSaving(false);
    }
  };

  const saveInitialBalance=async()=>{
    const efectivo=decimalNumber(balanceForm.efectivo);
    const banco=decimalNumber(balanceForm.transferencia);
    if(efectivo<=0&&banco<=0){showError("Ingresa al menos un saldo inicial");return;}
    try{
      setSaving(true);
      const response=await fetch(`${API_BASE}/saldo-inicial/`,{
        method:"POST",
        headers:{...authHeaders,"Content-Type":"application/json"},
        body:JSON.stringify({
          efectivo:balanceForm.efectivo||"0",
          transferencia:balanceForm.transferencia||"0",
          observacion:balanceForm.observacion
        }),
        credentials:"include"
      });
      const data=await responseData(response);
      if(!response.ok){showError(errorMessage(data,"No se pudo registrar el saldo inicial"));return;}
      showSuccess(data.mensaje||"Saldo inicial registrado correctamente");
      setBalanceModal(false);
      setBalanceForm(initialBalance);
      await reload();
    }catch{
      showError("Error de conexión");
    }finally{
      setSaving(false);
    }
  };

  const outlinedButtonSx={
    minHeight:40,
    px:2.4,
    borderRadius:"12px",
    textTransform:"none",
    fontWeight:700,
    color:dashboardTheme.button_color,
    borderColor:dashboardTheme.button_color,
    boxShadow:"none",
    "&:hover":{
      borderColor:dashboardTheme.button_color,
      bgcolor:`${dashboardTheme.button_color}12`,
      boxShadow:"none"
    }
  };

  const dialogPaper={
    borderRadius:"16px",
    bgcolor:dashboardTheme.card_background,
    border:`1px solid ${dashboardTheme.border_color}`,
    boxShadow:"none"
  };

  const selectMenuProps={
    disableScrollLock:true,
    PaperProps:{
      elevation:0,
      sx:{
        mt:.5,
        bgcolor:dashboardTheme.card_background,
        color:dashboardTheme.text_color,
        border:`1px solid ${dashboardTheme.border_color}`,
        boxShadow:"none"
      }
    }
  };

  if(!canCreate)return null;

  return(
    <>
      <Box sx={{display:"flex",gap:1,flexWrap:"wrap"}}>
        <Button variant="outlined" startIcon={<AccountBalanceWalletRoundedIcon/>} onClick={()=>setBalanceModal(true)} sx={outlinedButtonSx}>Saldo inicial</Button>
        <Button variant="outlined" startIcon={<SwapHorizRoundedIcon/>} onClick={()=>setTransferModal(true)} sx={outlinedButtonSx}>Traspaso</Button>
        <Button variant="contained" startIcon={<AddRoundedIcon/>} onClick={()=>setManualModal(true)} sx={dashboardTheme.buttonStyles.contained}>Nuevo movimiento</Button>
      </Box>

      <Dialog open={manualModal} onClose={closeManual} fullWidth maxWidth="sm" PaperProps={{sx:dialogPaper}}>
        <DialogTitle sx={{color:dashboardTheme.text_color,fontSize:20,fontWeight:600}}>Nuevo movimiento</DialogTitle>
        <DialogContent sx={{pt:"10px!important"}}>
          <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",sm:"1fr 1fr"},gap:1.5}}>
            <FormControl size="small" sx={dashboardTheme.inputStyles}>
              <InputLabel>Tipo</InputLabel>
              <Select label="Tipo" value={manualForm.tipo} onChange={e=>updateManual("tipo",e.target.value)} MenuProps={selectMenuProps}>
                <MenuItem value="INGRESO">Ingreso</MenuItem>
                <MenuItem value="EGRESO">Retiro / Egreso</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={dashboardTheme.inputStyles}>
              <InputLabel>Medio</InputLabel>
              <Select label="Medio" value={manualForm.medio} onChange={e=>updateManual("medio",e.target.value)} MenuProps={selectMenuProps}>
                <MenuItem value="EFECTIVO">Efectivo</MenuItem>
                <MenuItem value="TRANSFERENCIA">Banco</MenuItem>
              </Select>
            </FormControl>
            <TextField size="small" label="Monto" value={manualForm.monto} onChange={e=>updateManual("monto",e.target.value)} inputProps={{inputMode:"decimal"}} sx={dashboardTheme.inputStyles}/>
            <TextField size="small" label="Concepto" value={manualForm.concepto} onChange={e=>updateManual("concepto",e.target.value)} sx={dashboardTheme.inputStyles}/>
            <TextField size="small" label="Observación" value={manualForm.observacion} onChange={e=>updateManual("observacion",e.target.value)} multiline minRows={2} sx={{gridColumn:{xs:"1",sm:"1 / -1"},...dashboardTheme.inputStyles}}/>
          </Box>
        </DialogContent>
        <DialogActions sx={{px:3,pb:2}}>
          <Button variant="outlined" disabled={saving} onClick={closeManual} sx={outlinedButtonSx}>Cancelar</Button>
          <Button variant="contained" disabled={saving} onClick={saveManualMovement} sx={dashboardTheme.buttonStyles.contained}>{saving?"Registrando...":"Registrar"}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={transferModal} onClose={closeTransfer} fullWidth maxWidth="sm" PaperProps={{sx:dialogPaper}}>
        <DialogTitle sx={{color:dashboardTheme.text_color,fontSize:20,fontWeight:600}}>Traspaso entre fondos</DialogTitle>
        <DialogContent sx={{pt:"10px!important"}}>
          <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",sm:"1fr 1fr"},gap:1.5}}>
            <FormControl size="small" sx={dashboardTheme.inputStyles}>
              <InputLabel>Origen</InputLabel>
              <Select
                label="Origen"
                value={transferForm.medio_origen}
                onChange={e=>{
                  const origen=e.target.value;
                  setTransferForm(prev=>({...prev,medio_origen:origen,medio_destino:origen==="EFECTIVO"?"TRANSFERENCIA":"EFECTIVO"}));
                }}
                MenuProps={selectMenuProps}
              >
                <MenuItem value="EFECTIVO">Efectivo</MenuItem>
                <MenuItem value="TRANSFERENCIA">Banco</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={dashboardTheme.inputStyles}>
              <InputLabel>Destino</InputLabel>
              <Select label="Destino" value={transferForm.medio_destino} onChange={e=>updateTransfer("medio_destino",e.target.value)} MenuProps={selectMenuProps}>
                <MenuItem value="EFECTIVO">Efectivo</MenuItem>
                <MenuItem value="TRANSFERENCIA">Banco</MenuItem>
              </Select>
            </FormControl>
            <TextField size="small" label="Monto" value={transferForm.monto} onChange={e=>updateTransfer("monto",e.target.value)} inputProps={{inputMode:"decimal"}} sx={dashboardTheme.inputStyles}/>
            <TextField size="small" label="Referencia" value={transferForm.referencia} onChange={e=>updateTransfer("referencia",e.target.value)} sx={dashboardTheme.inputStyles}/>
            <TextField size="small" label="Concepto" value={transferForm.concepto} onChange={e=>updateTransfer("concepto",e.target.value)} sx={{gridColumn:{xs:"1",sm:"1 / -1"},...dashboardTheme.inputStyles}}/>
            <TextField size="small" label="Observación" value={transferForm.observacion} onChange={e=>updateTransfer("observacion",e.target.value)} multiline minRows={2} sx={{gridColumn:{xs:"1",sm:"1 / -1"},...dashboardTheme.inputStyles}}/>
          </Box>
        </DialogContent>
        <DialogActions sx={{px:3,pb:2}}>
          <Button variant="outlined" disabled={saving} onClick={closeTransfer} sx={outlinedButtonSx}>Cancelar</Button>
          <Button variant="contained" disabled={saving} onClick={saveTransfer} sx={dashboardTheme.buttonStyles.contained}>{saving?"Registrando...":"Registrar"}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={balanceModal} onClose={closeBalance} fullWidth maxWidth="sm" PaperProps={{sx:dialogPaper}}>
        <DialogTitle sx={{color:dashboardTheme.text_color,fontSize:20,fontWeight:600}}>Saldo inicial</DialogTitle>
        <DialogContent sx={{pt:"10px!important"}}>
          <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",sm:"1fr 1fr"},gap:1.5}}>
            <TextField size="small" label="Efectivo" value={balanceForm.efectivo} onChange={e=>updateBalance("efectivo",e.target.value)} inputProps={{inputMode:"decimal"}} sx={dashboardTheme.inputStyles}/>
            <TextField size="small" label="Banco" value={balanceForm.transferencia} onChange={e=>updateBalance("transferencia",e.target.value)} inputProps={{inputMode:"decimal"}} sx={dashboardTheme.inputStyles}/>
            <TextField size="small" label="Observación" value={balanceForm.observacion} onChange={e=>updateBalance("observacion",e.target.value)} multiline minRows={2} sx={{gridColumn:{xs:"1",sm:"1 / -1"},...dashboardTheme.inputStyles}}/>
          </Box>
        </DialogContent>
        <DialogActions sx={{px:3,pb:2}}>
          <Button variant="outlined" disabled={saving} onClick={closeBalance} sx={outlinedButtonSx}>Cancelar</Button>
          <Button variant="contained" disabled={saving} onClick={saveInitialBalance} sx={dashboardTheme.buttonStyles.contained}>{saving?"Registrando...":"Registrar"}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}